import { useEffect, useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store';
import { 
  useGetPaymentMethodsQuery,
  useGetFeeStructureQuery,
  useLazyGetLeaseQuery,
} from '@/store/api';
import {
  setSelectedPaymentMethod,
  setCalculatedFees,
  setPayingWithSourceId,
  setAmountToPay,
  setIsCustomAmount,
} from '@/store/slices/residents/paymentsSlice';
import {
  selectPayingForLeaseId,
  selectPayingWithSourceId,
  selectSelectedPaymentMethod,
  selectAmountToPay,
  selectIsCustomAmount,
  selectCalculatedFees,
} from '@/store/selectors/paymentsSelectors';
import {
  getCardConnectCardName,
  getPaymentMethodLabel,
  getPaymentMethodKey,
  hasPaymentMethodCardExpired,
  IPaymentsFeeStructure,
} from '@/utils/residents/paymentUtils';
import { usePaymentFeeCalculation } from '@/features/payments/hooks/usePaymentValidation';
import { useEntrataIntegrationDisabled } from '@/features/payments/hooks/useEntrataIntegrationDisabled';
import { useCompletePaymentValidation } from './usePaymentValidation';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';

/**
 * Enhanced payment data hook matching web implementation
 * Provides centralized payment data management with validation
 */
export const usePaymentData = (residentId?: number) => {
  const dispatch = useAppDispatch();
  
  // Selectors
  const leaseId = useAppSelector(selectPayingForLeaseId);
  const payingWithSourceId = useAppSelector(selectPayingWithSourceId);
  const selectedPaymentMethod = useAppSelector(selectSelectedPaymentMethod);
  const amountToPay = useAppSelector(selectAmountToPay);
  const isCustomAmount = useAppSelector(selectIsCustomAmount);
  const calculatedFees = useAppSelector(selectCalculatedFees);
  
  // Auth and property data
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const property = useAppSelector((s) => (s as any).property?.current);
  const entrataDisabled = useEntrataIntegrationDisabled();
  
  // API queries - using regular queries for automatic cache invalidation
  const leaseKey = useMemo(() => String(leaseId || ''), [leaseId]);
  
  // Fetch balance via dedicated endpoint (works for all PMS types)
  const paymentBalance = usePaymentBalance(leaseKey);
  const isLoadingBalance = paymentBalance.isLoading;
  const balanceError = paymentBalance.error;
  
  // Use regular queries for automatic refresh when cache is invalidated
  const paymentMethodsResponse = useGetPaymentMethodsQuery();
  const feeStructureResponse = useGetFeeStructureQuery();
  const [fetchLease, leaseResponse] = useLazyGetLeaseQuery();
  
  // Fetch lease data on mount and when dependencies change
  useEffect(() => {
    if (leaseId) {
      fetchLease({ leaseId, includeTransactions: false, includePayments: true }, true);
    }
  }, [leaseId, fetchLease]);
  
  // Process API responses
  const paymentMethods: any[] = useMemo(() => {
    return (paymentMethodsResponse.data as any)?.response || 
           (paymentMethodsResponse.data as any) || 
           [];
  }, [paymentMethodsResponse.data]);
  
  const feeStructure: IPaymentsFeeStructure | null = useMemo(() => {
    return (feeStructureResponse.data as any)?.response || 
           (feeStructureResponse.data as any) || 
           null;
  }, [feeStructureResponse.data]);
  
  const leaseUsers: any[] = useMemo(() => {
    return (leaseResponse.data as any)?.response?.lease?.users || [];
  }, [leaseResponse.data]);
  
  // Determine preferred payment method
  const preferredPaymentMethod = useMemo(() => {
    if (payingWithSourceId) {
      return paymentMethods.find(method => Number(method.sourceId) === payingWithSourceId);
    }
    return paymentMethods.find(method => method.isDefault) || paymentMethods[0];
  }, [paymentMethods, payingWithSourceId]);
  
  // Update selected payment method when preferred method changes
  useEffect(() => {
    if (preferredPaymentMethod && (!selectedPaymentMethod || selectedPaymentMethod.id !== preferredPaymentMethod.id)) {
      dispatch(setSelectedPaymentMethod(preferredPaymentMethod));
    }
  }, [preferredPaymentMethod, selectedPaymentMethod, dispatch]);
  
  // Update paying with source ID when preferred method changes
  useEffect(() => {
    if (preferredPaymentMethod?.sourceId && !payingWithSourceId) {
      dispatch(setPayingWithSourceId(Number(preferredPaymentMethod.sourceId)));
    }
  }, [preferredPaymentMethod?.sourceId, payingWithSourceId, dispatch]);
  
  // Calculate fees when amount or payment method changes (centralized via hook)
  const feeCalculation = usePaymentFeeCalculation(
    amountToPay,
    selectedPaymentMethod,
    feeStructure,
    property,
  );
  
  // Update calculated fees in store
  useEffect(() => {
    dispatch(setCalculatedFees(feeCalculation));
  }, [feeCalculation, dispatch]);
  
  const balanceDue = useMemo(() => {
    const total = paymentBalance.balanceDue;
    return total < 0 ? 0 : total;
  }, [paymentBalance.balanceDue]);
  
  // User information for payment on behalf of
  const primaryUser = useMemo(() => {
    return leaseUsers.find((u) => u?.LeaseCustomer?.customerType === 'primary') || leaseUsers[0];
  }, [leaseUsers]);
  
  const isOwner = useMemo(() => {
    return authUser?.id === residentId;
  }, [authUser?.id, residentId]);
  
  const displayName = useMemo(() => {
    const firstName = primaryUser?.firstName || authUser?.firstName || '';
    const lastName = primaryUser?.lastName || authUser?.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }, [primaryUser, authUser]);
  
  // Payment method details
  const paymentMethodDetails = useMemo(() => {
    if (!selectedPaymentMethod) {
      return {
        name: '',
        type: '',
        last4: '',
        cardTypeName: '',
        isExpired: false,
        isBlocked: false,
        label: 'No payment method',
      };
    }
    
    return {
      name: selectedPaymentMethod.name || '',
      type: selectedPaymentMethod.type || '',
      last4: selectedPaymentMethod.last4 || '',
      cardTypeName: getCardConnectCardName(selectedPaymentMethod.accttype || ''),
      isExpired: hasPaymentMethodCardExpired(selectedPaymentMethod.expiry),
      isBlocked: Boolean(selectedPaymentMethod.blockedAt),
      label: getPaymentMethodLabel(selectedPaymentMethod),
      methodKey: getPaymentMethodKey(selectedPaymentMethod),
    };
  }, [selectedPaymentMethod]);
  
  // Loading states
  const isLoading = useMemo(() => {
    return isLoadingBalance || 
           paymentMethodsResponse.isLoading || 
           feeStructureResponse.isLoading ||
           leaseResponse.isLoading;
  }, [isLoadingBalance, paymentMethodsResponse.isLoading, feeStructureResponse.isLoading, leaseResponse.isLoading]);
  
  const isFetching = useMemo(() => {
    return paymentMethodsResponse.isFetching || 
           feeStructureResponse.isFetching ||
           leaseResponse.isFetching;
  }, [paymentMethodsResponse.isFetching, feeStructureResponse.isFetching, leaseResponse.isFetching]);
  
  // Validation
  const validation = useCompletePaymentValidation(
    amountToPay,
    balanceDue,
    isCustomAmount,
    selectedPaymentMethod,
    feeStructure,
    property
  );
  
  // Fee structure display
  const feeStructureDisplay = useMemo(() => {
    if (!feeStructure) {
      return {
        creditCard: '—',
        debitCard: '—',
        bankAccount: '—',
      };
    }
    
    const creditFeeText = feeStructure.creditCard?.includes('%')
      ? feeStructure.creditCard
      : property?.creditCardFeeRate
      ? `${Number(property.creditCardFeeRate * 100).toFixed(2)}%`
      : '—';
      
    const debitFeeText = feeStructure.debitCard?.includes('%')
      ? feeStructure.debitCard
      : property?.debitCardFeeRate
      ? `${Number(property.debitCardFeeRate * 100).toFixed(2)}%`
      : '—';
      
    const achFeeText = feeStructure.bankAccount
      ? feeStructure.bankAccount
      : property?.achFee
      ? `$${property.achFee}`
      : '—';
    
    return {
      creditCard: creditFeeText,
      debitCard: debitFeeText,
      bankAccount: achFeeText,
    };
  }, [feeStructure, property]);
  
  // Actions
  const updateAmountToPay = useCallback((amount: number) => {
    dispatch(setAmountToPay(amount));
  }, [dispatch]);
  
  const updateIsCustomAmount = useCallback((isCustom: boolean) => {
    dispatch(setIsCustomAmount(isCustom));
  }, [dispatch]);
  
  const updateSelectedPaymentMethod = useCallback((method: any) => {
    dispatch(setSelectedPaymentMethod(method));
    if (method?.sourceId) {
      dispatch(setPayingWithSourceId(Number(method.sourceId)));
    }
  }, [dispatch]);
  
  return {
    // Balance data
    balanceDue,
    
    // Payment method data
    paymentMethods,
    selectedPaymentMethod,
    preferredPaymentMethod,
    ...paymentMethodDetails,
    
    // Fee data
    feeStructure,
    feeStructureDisplay,
    calculatedFees,
    convenienceFee: feeCalculation.convenienceFee,
    totalCharge: feeCalculation.total,
    
    // User data
    leaseUsers,
    primaryUser,
    isOwner,
    displayName,
    
    // Payment state
    amountToPay,
    isCustomAmount,
    payingWithSourceId,
    
    // Loading states
    isLoading,
    isFetching,
    isLoadingPaymentMethod: paymentMethodsResponse.isLoading,
    isFetchingPaymentMethod: paymentMethodsResponse.isFetching,
    isSuccessPaymentMethod: paymentMethodsResponse.isSuccess,
    
    // Validation
    validation,
    canProceed: validation.canProceedToConfirmation,
    
    // Responses for advanced usage
    paymentMethodsResponse,
    feeStructureResponse,
    leaseResponse,
    
  // Actions
  updateAmountToPay,
  updateIsCustomAmount,
  updateSelectedPaymentMethod,
  refreshPaymentMethods: () => {
    paymentMethodsResponse.refetch();
    feeStructureResponse.refetch();
  },
  refreshBalance: paymentBalance.refreshBalance,
  
  // Errors
  balanceError,
  hasErrors: Boolean(balanceError || paymentMethodsResponse.error || feeStructureResponse.error),
  };
};
